import browser from "@raiden/library/constants/browser";
import {
  POSTS_TYPE_VALUE_ARTICLE,
  POSTS_TYPE_VALUE_PAGE,
} from "@raiden/library/constants/posts";
import { RESOURCES_VISIBILITY_VALUE_PUBLIC } from "@raiden/library/constants/resources";
import { usePreferences } from "@raiden/library/contexts/Preferences";
import { useApi } from "@raiden/library/hooks/useApi";
import { serverApiFetcher } from "@raiden/library/hooks/useApiFetcher";
import { useConfiguration } from "@raiden/library/hooks/useConfiguration";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import generateFrontUri from "@raiden/library/libraries/utils/generateFrontUrl";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import Seo from "../components/Seo";
import { Home } from "../containers/Home";

/**
 * @param {object} params
 * @param {number} params.environmentId
 * @param {number[] | string[]} [params.categoryIds]
 */
function getUrlArticleList({ environmentId, categoryIds }) {
  return generateApiUrl({
    id: "@api.posts.viewAny",
    query: {
      environment_id: [environmentId],
      type: [POSTS_TYPE_VALUE_ARTICLE],
      visibility: RESOURCES_VISIBILITY_VALUE_PUBLIC,
      category_id: categoryIds,
      page: 1,
      per_page: 250,
      fields: [
        "post.current_revision",
        "post.revision.cover",
        "post.categories",
      ],
    },
  });
}

/**
 * @param {object} params
 * @param {number} params.environmentId
 * @param {number[] | string[]} [params.categoryIds]
 */
function getUrlPageList({ environmentId, categoryIds }) {
  return generateApiUrl({
    id: "@api.posts.viewAny",
    query: {
      environment_id: [environmentId],
      type: [POSTS_TYPE_VALUE_PAGE],
      visibility: RESOURCES_VISIBILITY_VALUE_PUBLIC,
      category_id: categoryIds,
      page: 1,
      per_page: 250,
      fields: [
        "post.current_revision",
        "post.revision.cover",
        "post.categories",
      ],
    },
  });
}

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post[]>} [postArticleExpertiseListResponse]
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post[]>} [postPageListResponse]
 */
/**
 * @param {Props} props
 */
export default function Page({
  postArticleExpertiseListResponse,
  postPageListResponse,
}) {
  const intl = useIntl();

  const { bestEnvironment } = usePreferences();

  const { configuration } = useConfiguration();

  const supportAlias = process.env.NEXT_PUBLIC_POST_CATEGORY_ALIAS_SUPPORT;
  const fieldOfExpertiseAlias =
    process.env.NEXT_PUBLIC_POST_CATEGORY_ALIAS_FIELD_EXPERTISE;

  const postCategorySupport = useMemo(() => {
    return configuration?.posts_categories?.page?.find(
      (category) => category.alias === supportAlias,
    );
  }, [configuration?.posts_categories?.page, supportAlias]);

  const postCategoryFieldOfExpertise = useMemo(() => {
    return configuration?.posts_categories?.article?.find(
      (category) => category.alias === fieldOfExpertiseAlias,
    );
  }, [configuration?.posts_categories?.article, fieldOfExpertiseAlias]);

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Post").Post[]>} */
  const { swrResponse: swrResponseArticleExpertiseList } = useApi(
    bestEnvironment
      ? getUrlArticleList({
          environmentId: bestEnvironment.id,
          categoryIds: postCategoryFieldOfExpertise
            ? [postCategoryFieldOfExpertise.id]
            : [],
        })
      : null,
    {
      swrConfig: {
        fallbackData: postArticleExpertiseListResponse,
      },
    },
  );

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Post").Post[]>} */
  const { swrResponse: swrResponsePageList } = useApi(
    bestEnvironment
      ? getUrlPageList({
          environmentId: bestEnvironment.id,
          categoryIds: postCategorySupport ? [postCategorySupport.id] : [],
        })
      : null,
    {
      swrConfig: {
        fallbackData: postPageListResponse,
      },
    },
  );

  return (
    <>
      <Seo
        title={intl.formatMessage({
          defaultMessage: "Accueil",
        })}
        links={(locale) =>
          generateFrontUri({
            id: "@front.home",
            parameters: {
              locale,
            },
          })
        }
      />

      <Home
        swrResponsePageList={swrResponsePageList}
        swrResponseArticleExpertiseList={swrResponseArticleExpertiseList}
      />
    </>
  );
}

/**
 * @typedef {import("../types/Page").PageInitialProps & Props} InitialProps
 */
/**
 * @param {import("../types/AppContext").PageContext} context
 * @returns {Promise<InitialProps>}
 */
Page.getInitialProps = async function (context) {
  /** @type {InitialProps} */
  const values = {};

  const supportAlias = process.env.NEXT_PUBLIC_POST_CATEGORY_ALIAS_SUPPORT;
  const fieldOfExpertiseAlias =
    process.env.NEXT_PUBLIC_POST_CATEGORY_ALIAS_FIELD_EXPERTISE;

  const postCategorySupport =
    context.configuration?.posts_categories?.page?.find(
      (category) => category.alias === supportAlias,
    );

  const postCategoryFieldOfExpertise =
    context.configuration?.posts_categories?.article?.find(
      (category) => category.alias === fieldOfExpertiseAlias,
    );

  if (!browser) {
    const bestEnvironment =
      context.configuration?.environments?.length === 1
        ? context.configuration.environments[0]
        : undefined;

    if (bestEnvironment) {
      /** @type {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post[]> | undefined} */
      values.postArticleExpertiseListResponse = await serverApiFetcher(
        getUrlArticleList({
          environmentId: bestEnvironment.id,
          categoryIds: postCategoryFieldOfExpertise
            ? [postCategoryFieldOfExpertise.id]
            : [],
        }),
      ).catch((error) => {
        console.error("Failed to fetch article expertise list : ", error);
      });

      /** @type {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post[]> | undefined} */
      values.postPageListResponse = await serverApiFetcher(
        getUrlPageList({
          environmentId: bestEnvironment.id,
          categoryIds: postCategorySupport ? [postCategorySupport.id] : [],
        }),
      ).catch((error) => {
        console.error("Failed to fetch page list : ", error);
      });
    }
  }

  return values;
};
